import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Building2, Mail, MapPin, ArrowRight, LogIn, UserPlus } from 'lucide-react';
import { setCommitteeContext } from '@/utils/committeeContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommitteeLandingPage = () => {
  const { committeeSlug } = useParams();
  const [committee, setCommittee] = useState(null);
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await axios.get(`${API}/public/committees/${committeeSlug}`);
        const committeeData = response.data?.committee;
        setCommittee(committeeData);
        setScholarships(response.data?.scholarships || []);
        if (committeeData) {
          setCommitteeContext(committeeData);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLandingData();
  }, [committeeSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !committee) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg border border-slate-200 p-10 text-center max-w-lg">
          <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Committee not found</h1>
          <p className="text-slate-600 mb-6">The committee page you requested does not exist.</p>
          <Link to="/">
            <Button className="bg-greek-blue hover:bg-greek-blue-dark" data-testid="committee-landing-home-btn">
              Back to Main Site
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const landing = committee.landing_page || {};
  const heroTitle = landing.hero_title || `${committee.name} Scholarship Program`;
  const heroSubtitle = landing.hero_subtitle || committee.description || 'Apply for scholarships powered by your local AHEPA & DOP committee.';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="committee-landing-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={landing.banner_url ? { backgroundImage: `url(${landing.banner_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}
        >
          {!landing.banner_url && (
            <div className="absolute inset-0 bg-gradient-to-r from-greek-blue via-blue-900 to-greek-blue"></div>
          )}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="flex flex-col lg:flex-row lg:items-center gap-10">
            <div className="flex-1 text-white">
              <div className="flex items-center gap-3 mb-6">
                {landing.logo_url ? (
                  <img
                    src={landing.logo_url}
                    alt={`${committee.name} logo`}
                    className="w-14 h-14 rounded-lg object-cover border border-white/40"
                  />
                ) : (
                  <div className="w-14 h-14 bg-greek-gold rounded-lg flex items-center justify-center">
                    <Building2 className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/70">AHEPA Scholarship Committee</p>
                  <h2 className="text-xl font-semibold" data-testid="committee-landing-name">{committee.name}</h2>
                </div>
              </div>

              <h1 className="font-heading text-4xl md:text-5xl font-bold leading-tight" data-testid="committee-hero-title">
                {heroTitle}
              </h1>
              <p className="text-lg text-white/80 mt-4 max-w-2xl" data-testid="committee-hero-subtitle">
                {heroSubtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to={`/login?committee=${committee.slug}&redirect=/${committee.slug}/dashboard`}>
                  <Button className="bg-greek-gold hover:bg-yellow-500 text-white rounded-sm px-6" data-testid="committee-login-btn">
                    <LogIn className="w-4 h-4 mr-2" />
                    Log In
                  </Button>
                </Link>
                <Link to={`/signup?committee=${committee.slug}`}>
                  <Button variant="outline" className="border-white/60 text-white hover:bg-white/10 rounded-sm" data-testid="committee-signup-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Create Applicant Account
                  </Button>
                </Link>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 text-white w-full lg:w-96">
              <h3 className="font-semibold text-lg mb-3" data-testid="committee-scholarships-section-title">
                {landing.scholarships_title || 'Active Scholarships'}
              </h3>
              <p className="text-white/80 text-sm mb-6">
                {landing.scholarships_subtitle || 'Browse scholarship opportunities managed by this committee.'}
              </p>
              <div className="space-y-3">
                {scholarships.slice(0, 3).map((scholarship) => (
                  <div key={scholarship.id} className="flex items-center justify-between" data-testid={`committee-hero-scholarship-${scholarship.id}`}>
                    <div>
                      <p className="text-sm font-medium">{scholarship.name}</p>
                      <p className="text-xs text-white/70">Deadline: {new Date(scholarship.deadline).toLocaleDateString()}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/80" />
                  </div>
                ))}
                {scholarships.length === 0 && (
                  <p className="text-sm text-white/70">No active scholarships are available yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About + Contact */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-8" data-testid="committee-about-section">
          <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">{landing.about_title || 'About the Committee'}</h3>
          <p className="text-slate-600 leading-relaxed" data-testid="committee-about-description">
            {landing.about_description || committee.description || 'This committee manages scholarships and supports applicants throughout the process.'}
          </p>
        </div>
          <div className="bg-white rounded-lg border border-slate-200 p-8" data-testid="committee-contact-section">
          <h3 className="font-heading text-xl font-semibold text-slate-900 mb-4">Contact</h3>
          <div className="space-y-3 text-sm text-slate-600">
            <div className="flex items-center gap-3" data-testid="committee-contact-email">
              <Mail className="w-4 h-4 text-greek-blue" />
              <span>{landing.contact_email || 'committee@ahepa-scholarship.com'}</span>
            </div>
            <div className="flex items-center gap-3" data-testid="committee-contact-address">
              <MapPin className="w-4 h-4 text-greek-blue" />
              <span>{landing.contact_address || 'Local chapter address'}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Scholarships */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-heading text-2xl font-bold text-slate-900" data-testid="committee-scholarship-list-title">
              {landing.scholarships_title || 'Active Scholarships'}
            </h3>
            <p className="text-slate-600 mt-1" data-testid="committee-scholarship-list-subtitle">
              {landing.scholarships_subtitle || 'Apply to scholarships available through this committee.'}
            </p>
          </div>
          <Link to={`/login?committee=${committee.slug}&redirect=/${committee.slug}/dashboard`}>
            <Button variant="outline" className="rounded-sm" data-testid="committee-login-cta">
              Log in to Apply
            </Button>
          </Link>
        </div>

        {scholarships.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-10 text-center">
            <p className="text-slate-600">No active scholarships available at this time.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {scholarships.map((scholarship) => (
              <div key={scholarship.id} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`committee-scholarship-card-${scholarship.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">{scholarship.name}</h4>
                    <p className="text-sm text-slate-600 line-clamp-3">{scholarship.description}</p>
                  </div>
                  <span className="text-greek-gold font-semibold whitespace-nowrap">
                    ${scholarship.amount?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
                  <span>Deadline: {new Date(scholarship.deadline).toLocaleDateString()}</span>
                  <Link to={`/login?committee=${committee.slug}&redirect=/${committee.slug}/dashboard`}>
                    <Button size="sm" className="bg-greek-blue hover:bg-greek-blue-dark" data-testid={`committee-apply-btn-${scholarship.id}`}>
                      Apply Now
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CommitteeLandingPage;
